/**
 * Las decisiones del sello de aceptación (A-02, S-02) que vale la pena probar
 * aparte del componente que las pinta.
 */

/**
 * A partir de cuánto vale la pena decir ademas cuando sincronizo.
 *
 * Una aceptacion con conexion llega al servidor en segundos, y esos segundos no
 * dicen nada: son latencia. Lo que importa es la que se capturo sin red y
 * sincronizo bastante despues, porque ahi las dos horas cuentan historias
 * distintas y conviene verlas juntas.
 *
 * El umbral no puede ser «distinto minuto»: una aceptacion a las 19:10:41 que
 * el servidor resuelve a las 19:11:16 son 35 segundos de red, pero cruza el
 * minuto y salia marcada como capturada aparte. Pasa en datos reales.
 *
 * Dos minutos: el caso que se reporto el 01/10 tardo 3 min 10 s en salir de la
 * cola y con el umbral anterior —cinco minutos— no se alcanzaba a mostrar.
 */
const MINIMO_PARA_CONTAR_APARTE = 2 * 60 * 1000

/**
 * La hora que certifica el sello: cuando la persona firmo y acepto los
 * terminos, no cuando el dato llego al servidor.
 *
 * El sello tiene un objetivo legal, y si la aceptacion paso por la cola de
 * sincronizacion esas dos horas no son la misma. Mostrar la del servidor
 * contaba el momento equivocado.
 *
 * Si no hay hora de dispositivo —registros viejos, o una aceptacion hecha
 * desde la web— se cae a la del servidor, que es lo unico que hay.
 */
export function horaDeLaFirma(
  dispositivo: string | null | undefined,
  servidor: string | null | undefined,
): string | null {
  if (dispositivo && Number.isFinite(new Date(dispositivo).getTime())) {
    return dispositivo
  }
  return servidor ?? null
}

/**
 * Si ademas de la firma hay que decir cuando sincronizo.
 *
 * Solo cuando se capturo bastante antes: en obra se acepta sin conexion y puede
 * sincronizar mucho despues, y verlas juntas explica una diferencia que de otro
 * modo parece un error. Cuando son el mismo momento con latencia en medio,
 * repetirla es ruido.
 *
 * Una fecha que no se puede leer se trata como ausente: mostrar «Invalid Date»
 * en un registro legal es peor que no mostrar nada.
 */
export function sincronizoDespues(
  dispositivo: string | null | undefined,
  servidor: string | null | undefined,
): boolean {
  if (!dispositivo || !servidor) return false

  const a = new Date(dispositivo).getTime()
  const b = new Date(servidor).getTime()
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false

  return Math.abs(a - b) >= MINIMO_PARA_CONTAR_APARTE
}

/**
 * La versión de términos como se lee en pantalla: `v1.0 — Resguardo de herramienta`.
 *
 * Sin versión no hay nada que decir, aunque venga el título: lo que sostiene el
 * sello es saber qué texto se aceptó, y el título solo nombra el documento.
 */
export function etiquetaDeTerminos(
  version: string | null | undefined,
  titulo: string | null | undefined,
): string | null {
  if (!version) return null

  return titulo ? `v${version} — ${titulo}` : `v${version}`
}
